'use client';

import React, { useState, useTransition } from 'react';
import { Locate, Loader2 } from 'lucide-react';
import { Button } from '../../common/button';
import { cn } from '@/lib/utils/utils';
import { getGeolocationFromCoordinates } from '@/lib/server/geolocation/actions';
import { Geolocation } from '@/lib/server/geolocation/types';
interface GeolocationButtonProps {
  onLocationFound?: (location: Geolocation) => void;
  onError?: (error: string) => void;
  className?: string;
  variant?: 'default' | 'outline' | 'ghost';
  size?: 'sm' | 'default' | 'lg';
  disabled?: boolean;
  iconOnly?: boolean;
}
export default function GeolocationButton({
  onLocationFound,
  onError,
  className,
  variant = 'outline',
  size = 'default',
  disabled = false,
  iconOnly = false
}: GeolocationButtonProps) {
  const [isPending, startTransition] = useTransition();
  const [permissionState, setPermissionState] = useState<'unknown' | 'granted' | 'denied' | 'prompt'>('unknown');
  const handleGetLocation = () => {
    if (!navigator.geolocation) {
      onError?.('Geolocation is not supported by this browser.');
      return;
    }
    startTransition(async () => {
      try {
        // Check permission state first
        if ('permissions' in navigator) {
          const permission = await navigator.permissions.query({
            name: 'geolocation' as PermissionName
          });
          setPermissionState(permission.state);
          if (permission.state === 'denied') {
            onError?.('Location access is denied. Please enable location services in your browser settings.');
            return;
          }
        }
        navigator.geolocation.getCurrentPosition(async position => {
          try {
            const {
              latitude,
              longitude
            } = position.coords;

            // Convert coordinates to location using the existing service
            const result = await getGeolocationFromCoordinates({
              latitude,
              longitude
            });
            if (result.success && result.value) {
              onLocationFound?.(result.value);
              setPermissionState('granted');
            } else {
              onError?.('Unable to determine your location. Please try searching manually.');
            }
          } catch (error_0) {
            console.error('Error getting location from coordinates:', error_0);
            onError?.('Unable to determine your location. Please try searching manually.');
          }
        }, error_1 => {
          let errorMessage = 'Unable to get your location.';
          switch (error_1.code) {
            case error_1.PERMISSION_DENIED:
              errorMessage = 'Location access denied. Please allow location services to use this feature.';
              setPermissionState('denied');
              break;
            case error_1.POSITION_UNAVAILABLE:
              errorMessage = 'Location information is unavailable. Please try searching manually.';
              break;
            case error_1.TIMEOUT:
              errorMessage = 'Location request timed out. Please try again.';
              break;
            default:
              errorMessage = 'An unknown error occurred while getting your location.';
              break;
          }
          onError?.(errorMessage);
        }, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000 // 5 minutes
        });
      } catch (error) {
        console.error('Error in geolocation button:', error);
        onError?.('An error occurred while accessing your location.');
      }
    });
  };
  const getButtonText = () => {
    if (isPending) return 'Getting location...';
    if (permissionState === 'denied') return 'Location denied';
    return 'Use my location';
  };
  const getButtonIcon = () => {
    if (isPending) {
      return <Loader2 className="h-4 w-4 animate-spin" />;
    }
    return <Locate className="h-4 w-4" />;
  };
  return <Button type="button" variant={variant} size={size} onClick={handleGetLocation} disabled={disabled || isPending} className={cn('flex items-center', iconOnly ? 'gap-0' : 'gap-2', {
    'cursor-not-allowed opacity-50': permissionState === 'denied'
  }, className)} aria-label={iconOnly ? getButtonText() : undefined}>
      {getButtonIcon()}
      {!iconOnly && getButtonText()}
    </Button>;
}